import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Divider,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Box,
  Menu,
  ListItemButton,
} from "@mui/material";
import TitleIcon from "@mui/icons-material/Title";
import SearchIcon from "@mui/icons-material/Search";
import PrintIcon from "@mui/icons-material/Print";
import CancelIcon from "@mui/icons-material/Cancel";
import FindReplaceIcon from "@mui/icons-material/FindReplace";
import {
  HEADINGS,
  LOW_PRIORIRTY,
  RichTextAction,
  RICH_TEXT_OPTIONS,
  FONT_FAMILY_OPTIONS,
  RICH_TEXT_OPTIONS_SECOND_ROW,
  AVAILABLE_MERGE_FIELDS,
  GROUPS,
} from "../../../../constants/textEditor";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $getSelection,
  $isRangeSelection,
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  ElementFormatType,
  FORMAT_ELEMENT_COMMAND,
  FORMAT_TEXT_COMMAND,
  REDO_COMMAND,
  SELECTION_CHANGE_COMMAND,
  TextFormatType,
  UNDO_COMMAND,
  $createParagraphNode,
  $insertNodes,
  $createTextNode,
  CUT_COMMAND,
  COPY_COMMAND,
  PASTE_COMMAND,
  PasteCommandType,
  INDENT_CONTENT_COMMAND,
  OUTDENT_CONTENT_COMMAND,
  $isTextNode,
  COMMAND_PRIORITY_LOW,
} from "lexical";
import { TOGGLE_LINK_COMMAND, $isLinkNode } from "@lexical/link";
import {
  $getNearestBlockElementAncestorOrThrow,
  mergeRegister,
} from "@lexical/utils";
import {
  HeadingTagType,
  $createHeadingNode,
  $isHeadingNode,
  $createQuoteNode,
  $isQuoteNode,
} from "@lexical/rich-text";
import {
  $getSelectionStyleValueForProperty,
  $isAtNodeEnd,
  $patchStyleText,
  $setBlocksType,
  $wrapNodes,
} from "@lexical/selection";
import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  $isListNode,
  REMOVE_LIST_COMMAND,
  INSERT_CHECK_LIST_COMMAND,
} from "@lexical/list";
import { createPortal } from "react-dom";
import FloatingLinkEditor from "./floating-link";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import "./tool-bar.scss";
import FontSize from "../font-size-plugin/font-size-plugin";
import { ImagePayload, INSERT_IMAGE_COMMAND } from "../plugins/image-plugin";
import { $createHorizontalRuleNode } from "@lexical/react/LexicalHorizontalRuleNode";
import {
  INSERT_TABLE_COMMAND_PAYLOAD,
  InsertTableCommandPayload,
} from "../plugins/table-plugin";
import {
  BackgroundColorPicker,
  TextColorPicker,
} from "../plugins/color-plugin";
import { INSERT_MERGE_FIELD_COMMAND } from "../plugins/MergeFieldPlugin";
import { $generateHtmlFromNodes } from "@lexical/html";
import { FindReplaceContext } from "../plugins/FindReplacePlugin";
// import { useKeyBindings } from "../hooks/useKeyBindings";
import { $isDecoratorBlockNode } from "@lexical/react/LexicalDecoratorBlockNode";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";

function getSelectedNode(selection: any) {
  const anchor = selection.anchor;
  const focus = selection.focus;
  const anchorNode = selection.anchor.getNode();
  const focusNode = selection.focus.getNode();
  if (anchorNode === focusNode) {
    return anchorNode;
  }
  const isBackward = selection.isBackward();
  if (isBackward) {
    return $isAtNodeEnd(focus) ? anchorNode : focusNode;
  } else {
    return $isAtNodeEnd(anchor) ? focusNode : anchorNode;
  }
}

interface TableDialogProps {
  open: boolean;
  onClose: () => void;
  onInsert: (payload: InsertTableCommandPayload) => void;
}

const TableDialog: React.FC<TableDialogProps> = ({
  open,
  onClose,
  onInsert,
}) => {
  const [rows, setRows] = useState("3");
  const [columns, setColumns] = useState("3");
  const [includeHeaders, setIncludeHeaders] = useState(true);

  const handleInsert = () => {
    onInsert({ rows, columns, includeHeaders });
    onClose();
  };

  const handleRowsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Math.min(Number(e.target.value), 200);
    setRows(value.toString());
  };

  const handleColumnsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Math.min(Number(e.target.value), 200);
    setColumns(value.toString());
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Insert Table</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Rows"
            type="number"
            value={rows}
            onChange={handleRowsChange}
            inputProps={{ min: 1, max: 200 }}
          />
          <TextField
            label="Columns"
            type="number"
            value={columns}
            onChange={handleColumnsChange}
            inputProps={{ min: 1, max: 200 }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ color: "text.secondary" }}>
          Cancel
        </Button>
        <Button
          onClick={handleInsert}
          variant="contained"
          className="insert-button"
        >
          Insert
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface ImageDialogProps {
  open: boolean;
  onClose: () => void;
  onInsert: (payload: ImagePayload) => void;
}
const isValidImageUrl = (url: string): boolean => {
  try {
    const parsed = new URL(url);
    return /\.(png|jpe?g|gif|svg|webp|bmp|tiff)$/i.test(parsed.pathname);
  } catch {
    return false;
  }
};

const ImageDialog: React.FC<ImageDialogProps> = ({ open, onClose, onInsert }) => {
  const [src, setSrc] = useState("");
  const [altText, setAltText] = useState("");
  const [width, setWidth] = useState('200');
  const [height, setHeight] = useState('200');
  const [preview, setPreview] = useState<string>("");

  const [imageSrc, setImageSrc] = useState("");

  const [srcError, setSrcError] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const resetForm = () => {
  setSrc("");
  setAltText("");
  setWidth("");
  setHeight("");
  setImageSrc("");
  setPreview("");
  setSrcError("");
};

  const handleInsert = (finalSrc: string) => {
    // Validate URL if using external source
    if (!finalSrc) return;

    if (src && !isValidImageUrl(src)) {
      setSrcError("Please enter a valid image URL ending in .png, .jpg, etc.");
      return;
    }

    onInsert({
      src: finalSrc,
      altText,
      width: width ? Math.abs(parseInt(width)) : 200,
      height: height ? Math.abs(parseInt(height)) : 200,
    });


    // Reset fields
    setSrc("");
    setAltText("");
    setWidth('200');
    setHeight('200');
    setImageSrc("");
    setPreview("");
    setSrcError("");
    onClose();
  };

  const handleUpload = async (file: File) => {
    // Option 1: upload to your backend and get a public URL
    // Option 2: convert to base64 (for demo purpose)
    const reader = new FileReader();
    reader.onloadend = () => {
      if (reader.result) {
        setImageSrc(reader.result as string);
        // handleInsert(reader.result as string);
        // onClose();
      }
    };
    reader.readAsDataURL(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const objectUrl = URL.createObjectURL(file);
      setPreview(objectUrl); // set preview immediately
      handleUpload(file);
    }
  };

  const handleRemoveFile = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    setImageSrc("");
    setPreview("");
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Insert Image/QR Code</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {!imageSrc && (
            <TextField
              label="Image URL"
              value={src}
              onChange={(e) => {
                setSrc(e.target.value);
                setSrcError("");
              }}
              fullWidth
              error={!!srcError}
              helperText={srcError}
              disabled={!!imageSrc}
            />
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={handleFileChange}
          />
          {!imageSrc && !src && (
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={!!src}
              className="upload-button"
            >
              Upload from Device
            </button>
          )}

          {(imageSrc || isValidImageUrl(src)) && (
            <div className="preview-container">
              <div className="image-wrapper">
                <img src={preview} alt="Preview" className="preview-image" />

                <div className="close-image" onClick={handleRemoveFile}>
                  <CancelIcon fontSize="small" className="cancel-icon" />
                </div>
              </div>
            </div>
          )}
          <TextField
            label="Alt Text"
            value={altText}
            onChange={(e) => setAltText(e.target.value)}
            fullWidth
          />
          <Stack direction="row" spacing={2}>
            <TextField
              label="Width (px)"
              type="number"
              value={width}
              onChange={(e) => {
                const val = e.target.value;
                if (val === "") {
                  setWidth(""); // allow clearing the field
                  return;
                }
                const num = parseInt(val);
                if (!isNaN(num) && num <= 300) {
                  setWidth(val);
                }
              }}
              inputProps={{ min: 10, max: 300 }}
              sx={{ width: 150 }}
            />
            <TextField
              label="Height (px)"
              type="number"
              value={height}
             onChange={(e) => {
              const val = e.target.value;
              if (val === "") {
                setHeight(""); // allow clearing the field
                return;
              }
              const num = parseInt(val);
              if (!isNaN(num) && num <= 300) {
                setHeight(val);
              }
            }}
              inputProps={{ min: 10, max: 300 }}
              sx={{ width: 150 }}
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => {
            setSrc("");
            setAltText("");
            setWidth("");
            setHeight("");
            setImageSrc("");
            setPreview("");
            setSrcError("");
            onClose();
          }} 
          sx={{ color: "text.secondary" }}
        >
          Cancel
        </Button>
        <Button
          onClick={() => handleInsert(src || imageSrc)}
          variant="contained"
          disabled={!src && !imageSrc}
          className="insert-button"
        >
          Insert
        </Button>
      </DialogActions>
    </Dialog>
  );
};
export default function ToolbarPlugin({
  toggleFullscreen,
  isFullscreen,
  fieldName,
  includeOptOutText = false,
  templateMetaData,
}: {
  toggleFullscreen: () => void;
  isFullscreen: boolean;
  fieldName?: string;
   includeOptOutText?: boolean;
   templateMetaData?: any;
  
}) {
  const [editor] = useLexicalComposerContext();
  const [isLink, setIsLink] = useState<boolean>(false);
  const [blockType, setBlockType] = useState<string>("paragraph");
  const [fontSize, setFontSize] = useState<string>("16px");
  const [tableDialogOpen, setTableDialogOpen] = useState(false);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [activeFontFamily, setActiveFontFamily] = useState("Arial");
  const [selectedText, setSelectedText] = useState<string>("");

  const [dialogOpen, setDialogOpen] = useState(false);
  const ctx = useContext(FindReplaceContext);

  const [disableMap, setDisableMap] = useState<{ [id: string]: boolean }>({
    [RichTextAction.Undo]: true,
    [RichTextAction.Redo]: true,
  });
  const [selectionMap, setSelectionMap] = useState<{ [id: string]: boolean }>(
    {}
  );

  // const updateToolbar = () => {
  //   const selection = $getSelection();
  //   if ($isRangeSelection(selection)) {
  //     const anchorNode = selection.anchor.getNode();
  //     const element = anchorNode.getTopLevelElementOrThrow();
  //     const isList = $isListNode(element);
  //     const listType = isList ? element.getTag() : null;
  //     const isLink = $isLinkNode(element);

  //     const node = getSelectedNode(selection);
  //     const parent = node.getParent();

  //     const type = $isHeadingNode(element)
  //       ? element.getTag()
  //       : element.getType();

  //     setBlockType(type === "list" ? "paragraph" : type);

  //     setFontSize(
  //       $getSelectionStyleValueForProperty(selection, "font-size", "16px")
  //     );
  //     setActiveFontFamily(
  //       $getSelectionStyleValueForProperty(selection, "font-family", "Arial")
  //     );

  //     const newSelectionMap = {
  //       [RichTextAction.Bold]: selection.hasFormat("bold"),
  //       [RichTextAction.Italic]: selection.hasFormat("italic"),
  //       [RichTextAction.Underline]: selection.hasFormat("underline"),
  //       [RichTextAction.Strikethrough]: selection.hasFormat("strikethrough"),
  //       [RichTextAction.Superscript]: selection.hasFormat("superscript"),
  //       [RichTextAction.Subscript]: selection.hasFormat("subscript"),
  //       [RichTextAction.Code]: selection.hasFormat("code"),
  //       [RichTextAction.Highlight]: selection.hasFormat("highlight"),
  //       [RichTextAction.UnOrderList]: listType === "ul",
  //       [RichTextAction.OrderList]: listType === "ol",
  //       [RichTextAction.Link]: isLink,
  //     };
  //     if ($isLinkNode(parent) || $isLinkNode(node)) {
  //       newSelectionMap[RichTextAction.Link] = true;
  //       setIsLink(true);
  //     } else {
  //       newSelectionMap[RichTextAction.Link] = false;

  //       setIsLink(false);
  //     }
  //     setSelectionMap(newSelectionMap);
  //   }
  // };

  const updateToolbar = () => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      setSelectedText(selection.getTextContent());
      const anchorNode = selection.anchor.getNode();
      const topLevelElement = anchorNode.getTopLevelElement();

      // SAFETY CHECK: getTopLevelElement() can return null (instead of throwing)
      if (!topLevelElement) return;

      const isList = $isListNode(topLevelElement);
      const listType = isList ? topLevelElement.getTag() : null;
      const isLink = $isLinkNode(topLevelElement);

      const node = getSelectedNode(selection);
      const parent = node.getParent();

      const type = $isHeadingNode(topLevelElement)
        ? topLevelElement.getTag()
        : topLevelElement.getType();

      setBlockType(type === "list" ? "paragraph" : type);

      setFontSize(
        $getSelectionStyleValueForProperty(selection, "font-size", "16px")
      );
      setActiveFontFamily(
        $getSelectionStyleValueForProperty(selection, "font-family", "Arial")
      );

      const newSelectionMap = {
        [RichTextAction.Bold]: selection.hasFormat("bold"),
        [RichTextAction.Italic]: selection.hasFormat("italic"),
        [RichTextAction.Underline]: selection.hasFormat("underline"),
        [RichTextAction.Strikethrough]: selection.hasFormat("strikethrough"),
        [RichTextAction.Superscript]: selection.hasFormat("superscript"),
        [RichTextAction.Subscript]: selection.hasFormat("subscript"),
        [RichTextAction.Code]: selection.hasFormat("code"),
        [RichTextAction.Highlight]: selection.hasFormat("highlight"),
        [RichTextAction.UnOrderList]: listType === "ul",
        [RichTextAction.OrderList]: listType === "ol",
        [RichTextAction.Link]: isLink,
      };

      if ($isLinkNode(parent) || $isLinkNode(node)) {
        newSelectionMap[RichTextAction.Link] = true;
        setIsLink(true);
      } else {
        newSelectionMap[RichTextAction.Link] = false;
        setIsLink(false);
      }

      setSelectionMap(newSelectionMap);
    }
  };

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateToolbar();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          updateToolbar();
          return false;
        },
        LOW_PRIORIRTY
      ),
      editor.registerCommand(
        CAN_UNDO_COMMAND,
        (payload) => {
          setDisableMap((prev) => ({
            ...prev,
            [RichTextAction.Undo]: !payload,
          }));
          return false;
        },
        LOW_PRIORIRTY
      ),
      editor.registerCommand(
        CAN_REDO_COMMAND,
        (payload) => {
          setDisableMap((prev) => ({
            ...prev,
            [RichTextAction.Redo]: !payload,
          }));
          return false;
        },
        LOW_PRIORIRTY
      )
    );
  }, [editor]);

  //handle image paste
  useEffect(() => {
    return editor.registerCommand<ClipboardEvent>(
      PASTE_COMMAND,
      (event) => {
        const files = event.clipboardData?.files;
        if (files && files.length > 0) {
          const file = files[0];
          if (file.type.startsWith("image/")) {
            const reader = new FileReader();

            reader.onload = () => {
              const base64DataUrl = reader.result as string;

              editor.dispatchCommand(INSERT_IMAGE_COMMAND, {
                src: base64DataUrl, // ✅ now base64 instead of blob
                altText: "Image in letter template",
                height: 150,
                width: 200,
              });
            };

            reader.readAsDataURL(file); // ✅ convert to base64
            return true; // handled
          }
        }
        return false; // let others handle
      },
      COMMAND_PRIORITY_LOW
    );
  }, [editor]);


  const blobToBase64 = (blob: Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onloadend = () => {
        if (reader.result) {
          resolve(reader.result as string);
        } else {
          reject("FileReader finished but result is null/undefined");
        }
      };

      reader.onerror = (err) => {
        console.error("FileReader error:", err);
        reject(err);
      };
      reader.readAsDataURL(blob);
    });
  };

  const handlePaste = async () => {
    try {
      const items = await navigator.clipboard.read();

      for (const item of items) {
        // 🔹 Handle images
        for (const type of item.types) {
          if (type.startsWith("image/")) {
            const blob = await item.getType(type);
            // ✅ Wait for base64
            const base64DataUrl = await blobToBase64(blob);
            editor.dispatchCommand(INSERT_IMAGE_COMMAND, {
              src: base64DataUrl, // ✅ now guaranteed base64
              altText: "Image from Letter template",
              height: 150,
              width: 200,
            });

            return; // stop after inserting image
          }
        }

        // 🔹 Handle text
        if (item.types.includes("text/plain")) {
          const blob = await item.getType("text/plain");
          const text = await blob.text();

          editor.update(() => {
            const textNode = $createTextNode(text);
            $insertNodes([textNode]);
          });
          return;
        }
      }
    } catch (err) {
      console.error("Clipboard read failed, fallback to text only", err);

      const clipboardText = await navigator.clipboard.readText();
      if (clipboardText) {
        editor.update(() => {
          const textNode = $createTextNode(clipboardText);
          $insertNodes([textNode]);
        });
      }
    }
  };





  const onAction = (id: RichTextAction) => {
    switch (id) {
      case RichTextAction.OrderList:
        if (selectionMap[RichTextAction.OrderList]) {
          editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
        } else editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
        break;
      case RichTextAction.UnOrderList:
        if (selectionMap[RichTextAction.UnOrderList]) {
          editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
        } else editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
        break;
      case RichTextAction.Link:
        if (isLink) {
          editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
        } else editor.dispatchCommand(TOGGLE_LINK_COMMAND, "https://");
        break;
      case RichTextAction.Bold:
      case RichTextAction.Italic:
      case RichTextAction.Underline:
      case RichTextAction.Strikethrough:
      case RichTextAction.Superscript:
      case RichTextAction.Subscript:
      case RichTextAction.Highlight:
      case RichTextAction.Code:
        editor.dispatchCommand(
          FORMAT_TEXT_COMMAND,
          id.replace(
            /([A-Z])/,
            (match: any) => `_${match.toLowerCase()}`
          ) as TextFormatType
        );
        break;
      case RichTextAction.LeftAlign:
      case RichTextAction.CenterAlign:
      case RichTextAction.RightAlign:
      case RichTextAction.JustifyAlign:
        editor.dispatchCommand(
          FORMAT_ELEMENT_COMMAND,
          id.replace("Align", "").toLowerCase() as ElementFormatType
        );
        break;
      case RichTextAction.Undo:
        editor.dispatchCommand(UNDO_COMMAND, undefined);
        break;
      case RichTextAction.Redo:
        editor.dispatchCommand(REDO_COMMAND, undefined);
        break;
      case RichTextAction.CheckList:
        editor.dispatchCommand(INSERT_CHECK_LIST_COMMAND, undefined);
        break;
      case RichTextAction.Quote:
        editor.update(() => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            $wrapNodes(selection, () => $createQuoteNode());
          }
        });
        break;
      case RichTextAction.Table:
        setTableDialogOpen(true);
        break;
      case RichTextAction.Image:
        setImageDialogOpen(true);
        break;
      case RichTextAction.HorizontalRule:
        editor.update(() => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            const node = $createHorizontalRuleNode();
            $insertNodes([node]);
          }
        });
        break;
      case RichTextAction.Cut:
        editor.dispatchCommand(CUT_COMMAND, null);
        break;
      case RichTextAction.Copy:
        editor.dispatchCommand(COPY_COMMAND, null);
        break;
      case RichTextAction.Paste:
        handlePaste();
        break;
      case RichTextAction.DecreaseIndent:
        editor.dispatchCommand(OUTDENT_CONTENT_COMMAND, undefined);
        break;
      case RichTextAction.IncreaseIndent:
        editor.dispatchCommand(INDENT_CONTENT_COMMAND, undefined);
        break;
      case RichTextAction.ClearFormat:
        editor.update(() => {
          const selection = $getSelection();

          if ($isRangeSelection(selection)) {
            const anchor = selection.anchor;
            const focus = selection.focus;

            if (anchor.key === focus.key && anchor.offset === focus.offset) {
              return;
            }

            selection.format = 0;

            const nodes = selection.getNodes();

            nodes.forEach((node) => {
              if ($isTextNode(node)) {
                node.setFormat(0);

                if (node.getStyle() !== "") {
                  node.setStyle("");
                }
              } else if ($isHeadingNode(node) || $isQuoteNode(node)) {
                node.replace($createParagraphNode(), true);
              } else if ($isDecoratorBlockNode(node)) {
                node.setFormat("");
              }
            });

            $setBlocksType(selection, () => $createParagraphNode());
          }
        });

        break;

      default:
        console.warn(`Unhandled action: ${id}`);
    }
  };

  const handleTableInsert = (payload: InsertTableCommandPayload) => {
    editor.dispatchCommand(INSERT_TABLE_COMMAND_PAYLOAD, payload);
  };

  const handleImageInsert = (payload: ImagePayload) => {
    editor.dispatchCommand(INSERT_IMAGE_COMMAND, payload);
  };

  //   useKeyBindings({ onAction });
  const updateHeading = (heading: any) => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        if (heading === "Normal" || heading === "paragraph") {
          $wrapNodes(selection, () => $createParagraphNode());
        } else {
          $wrapNodes(selection, () => $createHeadingNode(heading));
        }
      }
    });
  };

  const onFontFamilySelect = useCallback(
    (fontFamily: any) => {
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          // This applies the style directly to the selected text
          $patchStyleText(selection, { "font-family": fontFamily });
        }
      });
    },
    [editor]
  );

  const handlePrint = () => {
    editor.update(() => {
      const htmlContent = $generateHtmlFromNodes(editor, null);

      const printWindow = window.open("", "_blank", "width=800,height=600");
      if (printWindow) {
        printWindow.document.write(`
          <html>
            <head>
              <title>Print Preview</title>
              <style>
                /* Optional print-specific styling */
                body {
                  font-family: sans-serif;
                  padding: 40px;
                  color: #000;
                }
                @media print {
                  body {
                    margin: 0;
                    padding: 0;
                  }
                }

                /* Ensure common formatting appears correctly */
                b, strong { font-weight: bold; }
                i, em { font-style: italic; }
                u { text-decoration: underline; }
                p { margin: 0 0 1em; }
                table{
                border-spacing:0px !important;}
              </style>
            </head>
            <body onload="window.print();">
              ${htmlContent}
              ${includeOptOutText
          ? `<p style="
                    bottom: 10;
                    left: 20;
                    right: 0;
                    color: #808080;
                    font-weight: 400;
                    font-size: 12px;
                    margin-top: 40px;
                  ">
                  ${templateMetaData?.find(
                      (item: any) => item.key === "OptOutText"
                    )?.value || ""
                    }
                </p>`
                    : ""
              }
            </body>
          </html>
        `);
        printWindow.document.close();
      }
    });
  };

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [submenuAnchorEl, setSubmenuAnchorEl] = useState<HTMLElement | null>(
    null
  );
  const [activeGroup, setActiveGroup] = useState<string | null>(null);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const closeMainMenu = () => {
    setAnchorEl(null);
    closeSubmenu();
  };

  const openSubmenu = (
    event: React.MouseEvent<HTMLElement>,
    group: string
  ): void => {
    setSubmenuAnchorEl(event.currentTarget);
    setActiveGroup(group);
  };
  const handleClose = () => {
    setAnchorEl(null);
    setSubmenuAnchorEl(null);
    setActiveGroup(null);
  };

  const closeSubmenu = () => {
    setSubmenuAnchorEl(null);
    setActiveGroup(null);
  };

  const handleFieldClick = (fieldKey: string) => {
    editor.dispatchCommand(INSERT_MERGE_FIELD_COMMAND, fieldKey);
    handleClose();
  };
  const filteredFields = AVAILABLE_MERGE_FIELDS.filter(
    (f) => f.group === activeGroup
  );

  return (
    <div className="tool-bar-wrapper">
      <Stack
        direction="row"
        spacing={1.3}
        alignItems="center"
        sx={{
          borderBottom: "1px solid #e0e0e0",
          // boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
        }}
        // className="tool-bar-wrapper"
      >
        {RICH_TEXT_OPTIONS.map(({ id, icon, label }, index) =>
          id === RichTextAction.Divider ? (
            <Divider key={`${id}${index}`} orientation="vertical" flexItem />
          ) : id === RichTextAction.ColorPicker ? (
            <TextColorPicker key={id} />
          ) : id === RichTextAction.BackgroundColorPicker ? (
            <BackgroundColorPicker key={id} />
          ) : (
            <Tooltip key={id} title={label || ""}>
              <IconButton
                size="small"
                onClick={() => onAction(id)}
                disabled={disableMap[id]}
                color={selectionMap[id] ? "primary" : "default"}
                sx={{ fontSize: 18 }}
              >
                {icon}
              </IconButton>
            </Tooltip>
          )
        )}

        <Tooltip title="Print">
          <IconButton onClick={handlePrint}>
            <PrintIcon />
          </IconButton>
        </Tooltip>

        <Tooltip title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}>
          <IconButton
            onClick={() => {
              toggleFullscreen();
            }}
          >
            {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
        </Tooltip>
        {/* </Stack> */}
        <TableDialog
          open={tableDialogOpen}
          onClose={() => setTableDialogOpen(false)}
          onInsert={handleTableInsert}
        />
        <ImageDialog
          open={imageDialogOpen}
          onClose={() => setImageDialogOpen(false)}
          onInsert={handleImageInsert}
        />
        {isLink &&
          createPortal(<FloatingLinkEditor editor={editor} />, document.body)}
      </Stack>

      <Stack direction="row" spacing={1.3} alignItems="center">
        <Select
          size="small"
          className="toolbar-item font-family"
          onChange={(e) => onFontFamilySelect(e.target.value)}
          value={activeFontFamily || ""}
          IconComponent={ExpandMoreIcon}
          renderValue={(selected) => (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              <TitleIcon />
              {selected}
            </div>
          )}
          sx={{
            border: "none",
            color: "rgba(0, 0, 0, 0.54)",
            boxShadow: "none",
            minWidth: 150,
            maxWidth: 150,
            overflow: "hidden",
            ".MuiSelect-select": {
              paddingLeft: "5px",
            },

            ".MuiOutlinedInput-notchedOutline": {
              border: "none",
            },
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.04)", // light gray on hover
              borderRadius: "10px",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              border: "none",
            },
          }}
        >
          {FONT_FAMILY_OPTIONS.map((family: string) => (
            <MenuItem key={family} value={family} sx={{ fontFamily: family }}>
              {family}
            </MenuItem>
          ))}
        </Select>
        <Divider orientation="vertical" flexItem />
        <FontSize
          selectionFontSize={fontSize.slice(0, -2)}
          editor={editor}
          disabled={false}
        />
        <Divider orientation="vertical" flexItem />
        <Select
          size="small"
          value={blockType || "paragraph"}
          onChange={(e) => updateHeading(e.target.value as HeadingTagType)}
          IconComponent={ExpandMoreIcon}
          sx={{
            border: "none",
            boxShadow: "none",
            color: "rgba(0, 0, 0, 0.54)",
            minWidth: 120,
            maxWidth: 120,
            overflow: "hidden",
            ".MuiOutlinedInput-notchedOutline": {
              border: "none",
            },
            ".MuiSelect-select": {
              paddingLeft: "5px",
            },
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.04)", // light gray on hover
              borderRadius: "10px",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              border: "none",
            },
          }}
        >
          {HEADINGS.map((heading: any) => (
            <MenuItem key={heading} value={heading}>
              {heading === "paragraph"
                ? "Normal"
                : heading.charAt(0).toUpperCase() + heading.slice(1)}
            </MenuItem>
          ))}
        </Select>
        <Divider orientation="vertical" flexItem />

        <>
          <Button
            size="medium"
            onClick={handleOpen}
            endIcon={<ExpandMoreIcon fontSize="large" />}
            sx={{
              textTransform: "none",
              color: "rgba(0, 0, 0, 0.54)",
              fontSize: "16px",
              fontWeight: 400,
              whiteSpace: "nowrap",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
                borderRadius: "10px",
              },
            }}
          >
            Merge Fields
          </Button>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={closeMainMenu}
          >

            {GROUPS.filter((group) =>
              fieldName ? group !== "Special Fields" : true
            ).map((group) => (
              <ListItemButton
                key={group}
                onClick={(e) => openSubmenu(e, group)}
                // onMouseEnter={(e) => openSubmenu(e, group)}
                onMouseDown={(e) => e.preventDefault()}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  cursor: "pointer",
                }}
              >
                {group} <ArrowRightIcon fontSize="small" />
              </ListItemButton>
            ))}
          </Menu>

          <Menu
            anchorEl={submenuAnchorEl}
            open={Boolean(submenuAnchorEl)}
            onClose={closeSubmenu}
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
          >
            {(filteredFields || [])
              .filter((field) => field.group === activeGroup)
              .map((field) => (
                <MenuItem
                  key={field.key}
                  onClick={() => handleFieldClick(field.key)}
                >
                  {field.label}
                </MenuItem>
              ))}
          </Menu>
        </>
        <Divider orientation="vertical" flexItem />
        {RICH_TEXT_OPTIONS_SECOND_ROW.map(({ id, icon, label }, index) =>
          id === RichTextAction.Divider ? (
            <Divider key={`${id}${index}`} orientation="vertical" flexItem />
          ) : id === RichTextAction.ColorPicker ? (
            <TextColorPicker key={id} />
          ) : id === RichTextAction.BackgroundColorPicker ? (
            <BackgroundColorPicker key={id} />
          ) : (
            <Tooltip key={id} title={label || ""}>
              <IconButton
                size="small"
                onClick={() => onAction(id)}
                disabled={disableMap[id]}
                color={selectionMap[id] ? "primary" : "default"}
                sx={{ fontSize: 18 }}
              >
                {icon}
              </IconButton>
            </Tooltip>
          )
        )}

        {/* <IconButton onClick={handlePrint}>
          <PrintIcon />
        </IconButton> */}

        <Tooltip title="Find & Replace (Ctrl+F)">
          <IconButton
            onClick={() => {
              if (ctx) {
                ctx.setModalOpen(true);
                ctx.setSearchTerm(selectedText);
              }
            }}
          >
            <FindReplaceIcon />
          </IconButton>
        </Tooltip>
        {/* </Stack> */}
      </Stack>
    </div>
  );
}
